import React, { useState, useEffect } from 'react';
import { format, isAfter, startOfDay } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { CalendarEntry } from '../types';
import { calendarAPI } from '../services/api';
import ImageDisplay from './ImageDisplay';
import ImagePreviewModal from './ImagePreviewModal';

type EntryStatus = 'locked' | 'unlocked' | 'ruined' | 'orgasm';

interface CalendarModalProps {
  date: Date;
  entry: CalendarEntry | null;
  onClose: () => void;
  canEdit: boolean;
}

const CalendarModal: React.FC<CalendarModalProps> = ({ date, entry, onClose, canEdit }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState<EntryStatus>(entry?.status || 'locked');
  const [description, setDescription] = useState(entry?.description || '');
  const [imagePath, setImagePath] = useState(entry?.image_path || '');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFutureDate, setIsFutureDate] = useState(false);

  useEffect(() => {
    const today = startOfDay(new Date());
    const selectedDate = startOfDay(date);
    setIsFutureDate(isAfter(selectedDate, today));
  }, [date]);

  useEffect(() => {
    // 初始化图片预览
    if (entry?.image_path) {
      setImagePreview(entry.image_path);
    }
  }, [entry]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        setError('请选择图片文件');
        return;
      }
      
      // 验证文件大小（限制为5MB）
      if (file.size > 20 * 1024 * 1024) {
        setError('图片文件大小不能超过20MB');
        return;
      }

      setSelectedFile(file);
      setError('');
      
      // 创建本地预览
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    setError('');
    
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await calendarAPI.uploadImage(selectedFile, dateStr);
      const uploadedImagePath = response.image_path;
      setImagePath(uploadedImagePath);
      setImagePreview(uploadedImagePath);
      setSelectedFile(null);
      
      // 显示成功消息
      setError(response.message || '图片上传成功');
      
      // 清空文件输入
      const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      
      // 延迟清除成功消息
      setTimeout(() => {
        setError('');
      }, 3000);
    } catch (err: any) {
      setError(err.message || '图片上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePath('');
    setImagePreview('');
    setSelectedFile(null);
    setError('');
    
    // 清空文件输入
    const fileInput = document.getElementById('imageUpload') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    
    setError('');
    setIsLoading(true);

    try {
      const entryData = {
        date: format(date, 'yyyy-MM-dd'),
        status,
        description: description || undefined,
        image_path: imagePath || undefined,
      };

      if (entry) {
        await calendarAPI.updateEntry(entry.id, entryData);
      } else {
        await calendarAPI.createEntry(entryData);
      }

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || '保存失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit || !entry || !window.confirm('确定要删除这条记录吗？')) {
      return;
    }

    try {
      await calendarAPI.deleteEntry(entry.id);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || '删除失败，请重试');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'locked':
        return 'bg-green-500';
      case 'unlocked':
        return 'bg-red-500';
      case 'ruined':
        return 'bg-yellow-500';
      case 'orgasm':
        return 'bg-black';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'locked':
        return '🔒';
      case 'unlocked':
        return '🔑';
      case 'ruined':
        return '🔒🐍';
      case 'orgasm':
        return '🐍';
      default:
        return '未知';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {format(date, 'yyyy年MM月dd日', { locale: zhCN })}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {!canEdit && isFutureDate && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span>未来日期的状态无法修改，请等待日期到达后再进行编辑。</span>
            </div>
          </div>
        )}

        {(!user || user?.role !== 'admin') && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <span>您正在查看小狗的记录，<a href="/login" className="text-blue-600 hover:text-blue-800 underline">登录？</a></span>
              </div>
            </div>
          </div>
        )}

        {!canEdit && !isFutureDate && user?.role !== 'admin' && user && (
          <div className="bg-gray-50 border border-gray-200 text-gray-700 px-4 py-3 rounded mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>您没有编辑权限，只有管理员可以修改状态记录。</span>
            </div>
          </div>
        )}

        {entry && (
          <div className="mb-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">当前状态：</span>
              <div className={`w-4 h-4 rounded-full ${getStatusColor(entry.status)}`}></div>
              <span className="text-sm font-medium">{getStatusText(entry.status)}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              状态
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(['locked', 'unlocked', 'ruined', 'orgasm'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  disabled={!canEdit}
                  className={`flex items-center justify-center p-3 rounded-lg border-2 transition-colors ${
                    status === s
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${!canEdit ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className={`w-4 h-4 rounded-full ${getStatusColor(s)} mr-2`}></div>
                  {getStatusText(s)}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              描述（可选）
            </label>
            <textarea
              id="description"
              rows={3}
              disabled={!canEdit}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="添加描述..."
            />
          </div>

          <div className="mb-6">
            {canEdit && (
              <>
                <label htmlFor="imageUpload" className="block text-sm font-medium text-gray-700 mb-2">
                  图片（可选）
                </label>
                
                <div className="space-y-3">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
                    <div className="flex flex-col items-center">
                      <svg 
                        className="w-8 h-8 text-gray-400 mb-2" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path 
                          strokeLinecap="round" 
                          strokeLinejoin="round" 
                          strokeWidth={2} 
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                        />
                      </svg>
                      <label htmlFor="imageUpload" className="cursor-pointer">
                        <span className="text-sm text-gray-600 hover:text-gray-800">
                          {selectedFile ? '点击重新选择图片' : '点击选择图片'}
                        </span>
                        <input
                          id="imageUpload"
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          disabled={isUploading}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG、GIF 格式，最大 20MB</p>
                    </div>
                    
                    {selectedFile && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-sm text-gray-700 truncate max-w-xs">
                              {selectedFile.name}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {selectedFile && (
                      <>
                        <button
                          type="button"
                          onClick={handleImageUpload}
                          disabled={isUploading}
                          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isUploading ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              上传中...
                            </span>
                          ) : (
                            '上传图片'
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedFile(null);
                            setImagePreview('');
                          }}
                          className="px-3 py-2 text-gray-600 hover:text-gray-800 focus:outline-none transition-colors"
                        >
                          取消
                        </button>
                      </>
                    )}
                    {(imagePath || entry?.image_path) && !selectedFile && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                      >
                        删除现有图片
                      </button>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {imagePreview && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                图片预览
              </label>
              <ImageDisplay
                imagePath={imagePreview}
                alt="日历条目图片"
                onClick={() => {
                  if (imagePreview) {
                    setPreviewImage(imagePreview);
                  }
                }}
              />
            </div>
          )}

          <div className="flex justify-between">
            <div>
              {entry && canEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-red-600 hover:text-red-700 focus:outline-none"
                >
                  删除
                </button>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-700 focus:outline-none"
              >
                取消
              </button>
              {canEdit && (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? '保存中...' : '保存'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* 图片预览模态框 */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        imageUrl={previewImage}
        alt="日历条目图片预览"
        onClose={() => setPreviewImage('')}
      />
    </div>
  );
};

export default CalendarModal;